// One browser record of the Individual Visitor & User Activity table.
// Sections A–H; each one loads its own data the first time it is opened
// (histories are never loaded with the table). Dates are shown in the
// platform's configured time zone.
import React, { useCallback, useEffect, useMemo, useState } from "react";
import Modal from "react-bootstrap/Modal";
import { Link } from "react-router-dom";
import {
  getIndividualVisitorAPI, getIndividualHistoryPageAPI, getIndividualHistoryLogAPI, getIndividualSessionsAPI,
  getIndividualLoginsAPI, getIndividualSecurityAPI, addIndividualAccountLinkAPI, removeIndividualAccountLinkAPI,
} from "../../../auth/liveApi";
import { roleDisplayLabel } from "../../../../utils/roleDisplayLabel";
import { formatCount } from "../LiveParts";
import { EVENT_LABELS, REFERRER_LABELS, TRAFFIC_LABELS } from "./individualFields";
import { durationLabel, formatDayTitle, formatInZone, formatTime, shortId } from "./individualFormat";
import { RiskBadge, StatusBadge, TypeBadge } from "./IndividualBadges";

const TABS = [
  ["overview", "A. Identity & Overview"],
  ["devices", "B. Devices & Sessions"],
  ["accounts", "C. Account Associations"],
  ["historypage", "D. Historypage"],
  ["historylog", "E. Historylog"],
  ["logins", "F. Login Attempts"],
  ["security", "G. Security Events"],
  ["restrictions", "H. Restrictions"],
];

const useLoader = (load, deps, enabled) => {
  const [state, setState] = useState({ loading: false, error: null, data: null });
  const run = useCallback(() => {
    const controller = new AbortController();
    setState((s) => ({ ...s, loading: true, error: null }));
    load(controller.signal)
      .then((data) => setState({ loading: false, error: null, data }))
      .catch((e) => { if (e.name !== "AbortError") setState((s) => ({ ...s, loading: false, error: e.message })); });
    return () => controller.abort();
  }, deps); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => (enabled ? run() : undefined), [enabled, run]);
  return [state, setState, run];
};

const Busy = ({ t }) => <div className="ia-state" aria-busy="true"><div className="ia-skeleton" style={{ width: "70%" }} /><div className="ia-skeleton" style={{ width: "50%" }} /><span className="live-sr">{t("loading", "Loading…")}</span></div>;
const Failed = ({ error, retry, t }) => <div className="ia-state is-error" role="alert"><span>{error}</span>{retry && <button type="button" className="live-btn live-btn-ghost" onClick={retry}>{t("retry", "Retry")}</button>}</div>;
const Hidden = ({ t }) => <div className="ia-state"><strong>{t("fieldOff", "This field is turned off")}</strong><span>{t("fieldOffHint", "Enable it in Individual Activity Tracking Settings to collect and show it. Stored data was not deleted.")}</span></div>;
const Empty = ({ text }) => <div className="ia-state"><span>{text}</span></div>;
const Item = ({ label, children }) => (children === undefined ? null : <div><dt>{label}</dt><dd>{children}</dd></div>);

/* ── A ── */
const Overview = ({ d, tz, t }) => {
  const v = d.visitor;
  const loc = [v.geo?.city, v.geo?.state, v.geo?.country].filter((x) => x !== undefined);
  return (
    <>
      <dl className="ia-dl">
        <Item label={t("visitorId", "Visitor ID")}><span className="ia-id">{v.visitorId}</span></Item>
        <Item label={t("cStatus", "Status")}><StatusBadge status={v.status} t={t} /></Item>
        <Item label={t("fType", "Visitor / user")}><TypeBadge type={v.visitorType} t={t} /></Item>
        <Item label={t("accountRole", "Account role")}>{v.accountRole ? roleDisplayLabel(v.accountRole) : "—"}</Item>
        {v.firstSeenAt !== undefined && <Item label={t("fFirstSeen", "First Seen")}>{formatInZone(v.firstSeenAt, tz)}</Item>}
        {v.lastSeenAt !== undefined && <Item label={t("fLastSeen", "Last Seen")}>{formatInZone(v.lastSeenAt, tz)}</Item>}
        {v.activeDays !== undefined && <Item label={t("activeDays", "Active days")}>{formatCount(v.activeDays)}</Item>}
        {v.totals?.visits !== undefined && <Item label={t("fVisits", "Total Visits")}>{formatCount(v.totals.visits)}</Item>}
        {v.totals?.sessions !== undefined && <Item label={t("fSessions", "Total Sessions")}>{formatCount(v.totals.sessions)}</Item>}
        {v.totals?.pageViews !== undefined && <Item label={t("fPageViews", "Total Page Views")}>{formatCount(v.totals.pageViews)}</Item>}
        {v.logins?.success !== undefined && <Item label={t("successLogins", "Successful logins")}>{formatCount(v.logins.success)}</Item>}
        {v.logins?.failed !== undefined && <Item label={t("failedLogins", "Failed logins")}>{formatCount(v.logins.failed)}</Item>}
        {v.logins?.lastLoginAt !== undefined && <Item label={t("lastLogin", "Last login")}>{formatInZone(v.logins.lastLoginAt, tz)}</Item>}
        {v.logins?.lastLogoutAt !== undefined && <Item label={t("lastLogout", "Last logout")}>{formatInZone(v.logins.lastLogoutAt, tz)}</Item>}
        {loc.length > 0 && <Item label={t("cLocation", "Location")}>{loc.filter((x) => x && x !== "Unknown").join(", ") || t("unknown", "Unknown")} <small>{t("approx", "(approximate)")}</small></Item>}
        {v.device && Object.keys(v.device).length > 0 && <Item label={t("cDevice", "Device")}>{[v.device.deviceType, v.device.os, v.device.browser].filter(Boolean).join(" · ") || "—"}</Item>}
        {v.referrer !== undefined && <Item label={t("fReferrer", "Referrer")}>{v.referrer?.category ? `${REFERRER_LABELS[v.referrer.category] || v.referrer.category} · ${v.referrer.host}` : t("direct", "Direct / none")}</Item>}
        {v.traffic?.class !== undefined && <Item label={t("cTraffic", "Traffic")}><span className={`ia-badge t-${v.traffic.class}`}>{TRAFFIC_LABELS[v.traffic.class]}</span>{v.traffic.reasons?.length ? <small> — {v.traffic.reasons.join(", ")}</small> : null}</Item>}
        <Item label={t("cSecurity", "Security")}><RiskBadge level={v.riskLevel} t={t} /> <small>{formatCount(v.securityEventCount)} {t("events", "event(s)")}</small></Item>
      </dl>
      {d.today && (
        <>
          <h4 className="ia-subhead">{t("today", "Today")} ({d.today.day})</h4>
          <div className="ia-chips">
            <span className="ia-chip">{formatCount(d.today.visits)} {t("visits", "visits")}</span>
            <span className="ia-chip">{formatCount(d.today.sessions)} {t("sessions", "sessions")}</span>
            <span className="ia-chip">{formatCount(d.today.pageViews)} {t("pageViews", "page views")}</span>
            <span className="ia-chip">{formatCount(d.today.logins)} {t("logins", "logins")}</span>
            <span className="ia-chip">{formatCount(d.today.failedLogins)} {t("failed", "failed")}</span>
          </div>
        </>
      )}
      <p className="ia-note">{t("collectedNote", "Collected: approximate location, device families, counters and the events listed in Historylog. Never collected: IP address, full user agent, passwords, tokens, form contents or query strings.")}</p>
    </>
  );
};

/* ── B ── */
export const SESSION_STATUS = (t) => ({
  active: t("ssActive", "Active"),
  ended_logout: t("ssLogout", "Ended — explicit logout"),
  ended_inferred: t("ssInferred", "Ended — inactivity (end time unknown)"),
  ended_identity_change: t("ssIdentity", "Ended — signed-in account changed or signed out"),
});

const Devices = ({ vk, d, tz, t }) => {
  const [rows, setRows] = useState(null);
  const [next, setNext] = useState(null);
  const [state, setState] = useState({ loading: false, error: null, hidden: false });
  const load = useCallback((before) => {
    setState((s) => ({ ...s, loading: true, error: null }));
    getIndividualSessionsAPI(vk, { before, limit: 20 })
      .then((r) => { setRows((prev) => (before ? [...(prev || []), ...r.rows] : r.rows)); setNext(r.nextBefore); setState({ loading: false, error: null, hidden: !!r.hidden }); })
      .catch((e) => setState({ loading: false, error: e.message, hidden: false }));
  }, [vk]);
  useEffect(() => { load(null); }, [load]);
  const labels = SESSION_STATUS(t);
  const v = d.visitor;
  return (
    <>
      <h4 className="ia-subhead">{t("thisBrowser", "This browser")}</h4>
      <dl className="ia-dl">
        <Item label={t("cDevice", "Device")}>{[v.device?.deviceType, v.device?.os, v.device?.browser].filter(Boolean).join(" · ") || "—"}</Item>
        <Item label={t("visitorId", "Visitor ID")}><span className="ia-id">{shortId(v.visitorId)}</span></Item>
      </dl>
      <h4 className="ia-subhead">{t("otherDevices", "Other browsers / devices of the same account(s)")}</h4>
      {d.otherDevices.length ? (
        <div className="ia-table-wrap"><table className="ia-table"><thead><tr><th>{t("visitorId", "Visitor ID")}</th><th>{t("cDevice", "Device")}</th><th>{t("fCountry", "Country")}</th><th>{t("lastActivity", "Last recorded activity")}</th></tr></thead>
          <tbody>{d.otherDevices.map((o) => (
            <tr key={o.visitorId}><td data-label={t("visitorId", "Visitor ID")}><span className="ia-id" title={o.visitorId}>{shortId(o.visitorId)}</span></td><td data-label={t("cDevice", "Device")}>{[o.device?.deviceType, o.device?.os, o.device?.browser].filter(Boolean).join(" · ") || "—"}</td><td data-label={t("fCountry", "Country")}>{o.geo?.country || "—"}</td><td data-label={t("lastActivity", "Last recorded activity")}>{formatInZone(o.lastActivityAt, tz)}</td></tr>
          ))}</tbody></table></div>
      ) : <p className="ia-note">{t("noOtherDevices", "No other browser record shares an account with this one.")}</p>}
      <h4 className="ia-subhead">{t("sessionsTitle", "Browser sessions")}</h4>
      {state.hidden ? <Hidden t={t} /> : state.error ? <Failed error={state.error} retry={() => load(null)} t={t} /> : !rows ? <Busy t={t} /> : !rows.length ? <Empty text={t("noSessions", "No session recorded.")} /> : (
        <div className="ia-table-wrap"><table className="ia-table">
          <thead><tr><th>{t("started", "Started")}</th><th>{t("lastObserved", "Last observed activity")}</th><th>{t("cStatus", "Status")}</th><th className="num">{t("cVisits", "Visits")}</th><th className="num">{t("cPageViews", "Page views")}</th><th>{t("session", "Session")}</th></tr></thead>
          <tbody>{rows.map((s) => (
            <tr key={s.sessionId}>
              <td data-label={t("started", "Started")}>{formatInZone(s.startedAt, tz)}</td>
              <td data-label={t("lastObserved", "Last observed activity")}>{formatInZone(s.lastSeenAt, tz)} <small className="live-muted">{durationLabel(s.startedAt, s.endedAt || s.lastSeenAt)}</small></td>
              <td data-label={t("cStatus", "Status")}>{labels[s.status] || s.status}{s.endedAt ? <small className="live-muted"> · {formatTime(s.endedAt, tz)}</small> : null}</td>
              <td data-label={t("cVisits", "Visits")} className="num">{s.totals?.visits !== undefined ? formatCount(s.totals.visits) : "—"}</td>
              <td data-label={t("cPageViews", "Page views")} className="num">{s.totals?.pageViews !== undefined ? formatCount(s.totals.pageViews) : "—"}</td>
              <td data-label={t("session", "Session")}><span className="ia-id" title={s.sessionId}>{shortId(s.sessionId)}</span></td>
            </tr>
          ))}</tbody>
        </table></div>
      )}
      {next && <button type="button" className="live-btn live-btn-ghost" style={{ marginTop: 8 }} disabled={state.loading} onClick={() => load(next)}>{t("loadMore", "Load more")}</button>}
      <p className="ia-note">{t("sessionRule", "A session ends at an explicit logout (exact time) or after the inactivity timeout (end time unknown: the last observed activity is shown instead).")}</p>
    </>
  );
};

/* ── C ── */
const Accounts = ({ vk, d, tz, t, reload }) => {
  const observed = d.visitor.associatedAccounts || [];
  const [form, setForm] = useState({ a: observed[0]?.userId || "", b: "", reason: "" });
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const [removing, setRemoving] = useState(null); // { id, reason }
  const submit = async (e) => {
    e.preventDefault();
    if (!/^[a-f\d]{24}$/i.test(form.b.trim())) { setMsg({ error: t("badUserId", "Enter the second account's user ID (24 characters).") }); return; }
    if (form.reason.trim().length < 5) { setMsg({ error: t("reasonRequired", "A reason (at least 5 characters) is required.") }); return; }
    setBusy(true); setMsg(null);
    try {
      await addIndividualAccountLinkAPI({ visitorId: vk, userIdA: form.a, userIdB: form.b.trim(), reason: form.reason.trim() });
      setMsg({ ok: t("linkAdded", "Accounts associated.") });
      setForm((f) => ({ ...f, b: "", reason: "" }));
      reload();
    } catch (err) { setMsg({ error: err.message }); } finally { setBusy(false); }
  };
  const remove = async () => {
    if (!removing || removing.reason.trim().length < 5) { setMsg({ error: t("reasonRequired", "A reason (at least 5 characters) is required.") }); return; }
    setBusy(true);
    try { await removeIndividualAccountLinkAPI(removing.id, { visitorId: vk, reason: removing.reason.trim() }); setRemoving(null); setMsg({ ok: t("linkRemoved", "Association removed.") }); reload(); } catch (err) { setMsg({ error: err.message }); } finally { setBusy(false); }
  };
  return (
    <>
      <h4 className="ia-subhead">{t("observedAccounts", "Accounts that signed in on this browser")}</h4>
      {observed.length ? (
        <div className="ia-table-wrap"><table className="ia-table"><thead><tr><th>{t("account", "Account")}</th><th>{t("fRole", "Account role")}</th><th>{t("firstOnBrowser", "First seen here")}</th><th>{t("lastOnBrowser", "Last seen here")}</th></tr></thead>
          <tbody>{observed.map((a) => (
            <tr key={a.userId}><td data-label={t("account", "Account")}><div className="ia-cell-main"><strong>{a.name}</strong><small>{a.email} · <span className="ia-id">{a.userId}</span></small></div></td><td data-label={t("fRole", "Account role")}>{roleDisplayLabel(a.role)}</td><td data-label={t("firstOnBrowser", "First seen here")}>{formatInZone(a.firstAt, tz)}</td><td data-label={t("lastOnBrowser", "Last seen here")}>{formatInZone(a.lastAt, tz)}</td></tr>
          ))}</tbody></table></div>
      ) : <p className="ia-note">{t("qNoAccount", "No account has signed in on this browser.")}</p>}
      <p className="ia-note">{t("noIpMerge", "Accounts are associated with a browser only when they actually signed in on it — never because of a shared IP address, location or browser type.")}</p>

      <h4 className="ia-subhead">{t("manualLinks", "Same person — associations set by an administrator")}</h4>
      {d.manualLinks.length ? (
        <ul className="ia-history">{d.manualLinks.map((l) => (
          <li key={l.id}>
            <strong>{l.a.name}</strong> ↔ <strong>{l.b.name}</strong> <small className="live-muted">— {l.reason} · {l.createdBy} · {formatInZone(l.createdAt, tz)}</small>{" "}
            {removing?.id === l.id ? (
              <span className="ia-form-grid" style={{ marginTop: 6 }}>
                <label className="ia-field"><span>{t("removeReason", "Reason for removal")}</span><input value={removing.reason} onChange={(e) => setRemoving({ ...removing, reason: e.target.value })} /></label>
                <span className="live-actions"><button type="button" className="live-btn live-btn-danger" disabled={busy} onClick={remove}>{t("confirmRemove", "Remove association")}</button><button type="button" className="live-btn live-btn-ghost" onClick={() => setRemoving(null)}>{t("cancel", "Cancel")}</button></span>
              </span>
            ) : <button type="button" className="live-link" onClick={() => setRemoving({ id: l.id, reason: "" })}>{t("remove", "Remove")}</button>}
          </li>
        ))}</ul>
      ) : <p className="ia-note">{t("noManualLinks", "No manual association.")}</p>}
      {observed.length > 0 && (
        <form onSubmit={submit} className="ia-form-grid" aria-label={t("addLink", "Associate two accounts")}>
          <label className="ia-field"><span>{t("accountA", "Account on this browser")}</span>
            <select value={form.a} onChange={(e) => setForm({ ...form, a: e.target.value })}>{observed.map((a) => <option key={a.userId} value={a.userId}>{a.name} ({a.email})</option>)}</select>
          </label>
          <label className="ia-field"><span>{t("accountB", "Other account (user ID)")}</span><input value={form.b} onChange={(e) => setForm({ ...form, b: e.target.value })} placeholder="66f1a2b3c4d5e6f708091a2b" /></label>
          <label className="ia-field"><span>{t("reason", "Reason (required)")}</span><input value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} placeholder={t("reasonPh", "e.g. confirmed by support ticket #123")} /></label>
          <button type="submit" className="live-btn" disabled={busy}>{t("associate", "Associate")}</button>
        </form>
      )}
      {msg?.error && <p className="live-error" role="alert">{msg.error}</p>}
      {msg?.ok && <p className="ia-ok" role="status">{msg.ok}</p>}
    </>
  );
};

/* ── D ── */
const HistoryPageTab = ({ vk, tz, t }) => {
  const [sort, setSort] = useState("views");
  const [raw, setRaw] = useState(false);
  const [state, , retry] = useLoader((signal) => getIndividualHistoryPageAPI(vk, { sort }, { signal }), [vk, sort], true);
  if (state.error) return <Failed error={state.error} retry={retry} t={t} />;
  if (!state.data) return <Busy t={t} />;
  if (state.data.hidden) return <Hidden t={t} />;
  const items = state.data.items;
  const max = Math.max(1, ...items.map((i) => i.count));
  return (
    <>
      <div className="ia-toolbar">
        <span className="live-muted">{t("hpSummary", "{value0} routes · {value1} page views", { value0: formatCount(state.data.totalRoutes), value1: formatCount(state.data.totalViews) })}</span>
        <div className="live-actions">
          <label className="live-muted" htmlFor="ia-hp-sort">{t("sortBy", "Sort by")}</label>
          <select id="ia-hp-sort" value={sort} onChange={(e) => setSort(e.target.value)}>
            <option value="views">{t("mostViewed", "Most viewed")}</option>
            <option value="recent">{t("recent", "Last visited")}</option>
            <option value="route">{t("route", "Route")}</option>
          </select>
          <button type="button" className="live-btn live-btn-ghost" onClick={() => setRaw((v) => !v)} aria-pressed={raw}>{raw ? t("tableView", "Table view") : t("rawView", "View field")}</button>
        </div>
      </div>
      {!items.length ? <Empty text={t("noPages", "No page view recorded.")} /> : raw ? (
        <pre className="ia-raw" aria-label="Historypage">{`Historypage: [\n${state.data.Historypage.map((pair) => `  ${JSON.stringify(pair)}`).join(",\n")}\n]`}</pre>
      ) : (
        <div className="ia-table-wrap"><table className="ia-table">
          <thead><tr><th>{t("route", "Route")}</th><th className="num">{t("views", "Views")}</th><th aria-hidden="true" /><th>{t("lastVisited", "Last visited")}</th></tr></thead>
          <tbody>{items.map((i) => (
            <tr key={i.route}>
              <td data-label={t("route", "Route")}><span className="ia-route">{i.route}</span></td>
              <td data-label={t("views", "Views")} className="num">{formatCount(i.count)}</td>
              <td aria-hidden="true" data-label=""><div className="ia-bar"><span style={{ width: `${Math.max(4, (i.count / max) * 100)}%` }} /></div></td>
              <td data-label={t("lastVisited", "Last visited")}>{formatInZone(i.lastAt, tz)}</td>
            </tr>
          ))}</tbody>
        </table></div>
      )}
      <p className="ia-note">{t("hpRule", "Each element is [route, number]. Query strings, hashes, tokens and e-mail addresses are removed before storage; a repeated route increments its number.")}</p>
    </>
  );
};

/* ── E ── */
const kindOf = (h) => {
  if (h.type === "login_success") return "k-login";
  if (["login_failed", "rate_limited", "session_token_rejected", "login_blocked_suspended"].includes(h.type)) return "k-fail";
  if (h.type === "security_event") return "k-security";
  if (h.type.startsWith("session_")) return h.inferred ? "k-session k-inferred" : "k-session";
  if (["account_link_added", "account_link_removed", "restriction"].includes(h.type)) return "k-admin";
  return "";
};

const TimelineItem = ({ h, tz, t }) => {
  const meta = [];
  if (h.sessionId) meta.push(<span key="s">{t("session", "Session")} <span className="ia-id">{shortId(h.sessionId)}</span></span>);
  if (h.endReason === "explicit_logout") meta.push(<span key="e">{t("endLogout", "Explicit logout")}</span>);
  if (h.endReason === "identity_change") meta.push(<span key="e">{t("endIdentity", "The signed-in account changed or signed out on this browser")}</span>);
  if (h.endReason === "inferred_timeout") meta.push(<span key="e">{t("endInferred", "Inactivity timeout — exact end unknown; time shown = last observed activity")}{h.detectedAt ? ` · ${t("detected", "detected")} ${formatInZone(h.detectedAt, tz)}` : ""}</span>);
  if (h.count > 1) meta.push(<span key="c">×{formatCount(h.count)} {h.lastAt ? `${t("until", "until")} ${formatTime(h.lastAt, tz)}` : ""}</span>);
  if (h.method === "device_verification") meta.push(<span key="m">{t("viaCode", "via new-device code")}</span>);
  if (h.reasonCode) meta.push(<span key="r">{h.reasonCode}</span>);
  if (h.type === "security_event") meta.push(<span key="sec">{h.securityType} · {h.severity} · {h.status}{h.count > 1 ? "" : ""}</span>);
  if (h.type === "restriction") meta.push(<span key="res">{h.action} · {h.level}{h.reason ? ` · ${h.reason}` : ""}</span>);
  if (h.detail) meta.push(<span key="d">{h.detail}</span>);
  return (
    <li className={`ia-tl-item ${kindOf(h)}`}>
      <div className="ia-tl-line"><span className="ia-tl-time">{formatTime(h.timestamp, tz)}</span><span className="ia-tl-label">{EVENT_LABELS[h.type] || h.type}</span></div>
      {meta.length > 0 && <div className="ia-tl-meta">{meta}</div>}
    </li>
  );
};

const mergeDays = (prev, next) => {
  if (!prev.length) return next;
  const out = [...prev];
  next.forEach((day) => {
    const last = out[out.length - 1];
    if (last && last.day === day.day) out[out.length - 1] = { ...last, histories: [...last.histories, ...day.histories] };
    else out.push(day);
  });
  return out;
};

const HistoryLogTab = ({ vk, t }) => {
  const [days, setDays] = useState(null);
  const [meta, setMeta] = useState({ nextBefore: null, timezone: null, hidden: false });
  const [state, setState] = useState({ loading: false, error: null });
  const [raw, setRaw] = useState(false);
  const load = useCallback((before) => {
    setState({ loading: true, error: null });
    getIndividualHistoryLogAPI(vk, { before, limit: 150 })
      .then((r) => { setDays((prev) => (before ? mergeDays(prev || [], r.Historylog) : r.Historylog)); setMeta({ nextBefore: r.nextBefore, timezone: r.timezone, hidden: !!r.hidden }); setState({ loading: false, error: null }); })
      .catch((e) => setState({ loading: false, error: e.message }));
  }, [vk]);
  useEffect(() => { load(null); }, [load]);
  if (state.error && !days) return <Failed error={state.error} retry={() => load(null)} t={t} />;
  if (!days) return <Busy t={t} />;
  if (meta.hidden) return <Hidden t={t} />;
  return (
    <>
      <div className="ia-toolbar">
        <span className="live-muted">{t("tzNote", "Grouped by day in {value0}", { value0: meta.timezone || "UTC" })}</span>
        <button type="button" className="live-btn live-btn-ghost" onClick={() => setRaw((v) => !v)} aria-pressed={raw}>{raw ? t("timelineView", "Timeline view") : t("rawView", "View field")}</button>
      </div>
      {!days.length ? <Empty text={t("noEvents", "No history recorded for this browser.")} /> : raw ? (
        <pre className="ia-raw" aria-label="Historylog">{`Historylog: ${JSON.stringify(days, null, 2)}`}</pre>
      ) : days.map((day) => (
        <section key={day.day} className="ia-day" aria-label={formatDayTitle(day.day)}>
          <header className="ia-day-head">
            <strong>{formatDayTitle(day.day)}</strong>
            {day.summary && (
              <span className="ia-chips">
                <span className="ia-chip">{formatCount(day.summary.visits)} {t("visits", "visits")}</span>
                <span className="ia-chip">{formatCount(day.summary.sessions)} {t("sessions", "sessions")}</span>
                <span className="ia-chip">{formatCount(day.summary.pageViews)} {t("pageViews", "page views")}</span>
                {day.summary.logins > 0 && <span className="ia-chip">{formatCount(day.summary.logins)} {t("logins", "logins")}</span>}
                {day.summary.failedLogins > 0 && <span className="ia-chip">{formatCount(day.summary.failedLogins)} {t("failed", "failed")}</span>}
              </span>
            )}
          </header>
          <ol className="ia-timeline">{day.histories.map((h, i) => <TimelineItem key={`${h.timestamp}-${h.type}-${i}`} h={h} tz={meta.timezone} t={t} />)}</ol>
        </section>
      ))}
      {meta.nextBefore && <button type="button" className="live-btn live-btn-ghost" disabled={state.loading} onClick={() => load(meta.nextBefore)}>{state.loading ? t("loading", "Loading…") : t("loadOlder", "Load older days")}</button>}
      {state.error && days && <p className="live-error" role="alert">{state.error}</p>}
    </>
  );
};

/* ── F ── */
const LoginsTab = ({ vk, tz, t }) => {
  const [state, , retry] = useLoader((signal) => getIndividualLoginsAPI(vk, { limit: 50 }, { signal }), [vk], true);
  if (state.error) return <Failed error={state.error} retry={retry} t={t} />;
  if (!state.data) return <Busy t={t} />;
  const c = state.data.counters || {};
  return (
    <>
      <dl className="ia-dl">
        {c.success !== undefined && <Item label={t("successLogins", "Successful logins")}>{formatCount(c.success)}</Item>}
        {c.failed !== undefined && <Item label={t("failedLogins", "Failed logins")}>{formatCount(c.failed)}</Item>}
        {c.lastLoginAt !== undefined && <Item label={t("lastLogin", "Last login")}>{formatInZone(c.lastLoginAt, tz)}</Item>}
        {c.lastLogoutAt !== undefined && <Item label={t("lastLogout", "Last logout")}>{formatInZone(c.lastLogoutAt, tz)}</Item>}
        {c.lastFailedAt !== undefined && <Item label={t("lastFailed", "Last failed attempt")}>{formatInZone(c.lastFailedAt, tz)}</Item>}
      </dl>
      {state.data.historyHidden ? <Hidden t={t} /> : !state.data.rows.length ? <Empty text={t("noLogins", "No sign-in activity recorded on this browser.")} /> : (
        <div className="ia-table-wrap"><table className="ia-table">
          <thead><tr><th>{t("when", "When")}</th><th>{t("event", "Event")}</th><th className="num">{t("attempts", "Attempts")}</th><th>{t("detail", "Detail")}</th></tr></thead>
          <tbody>{state.data.rows.map((r, i) => (
            <tr key={`${r.at}-${i}`}>
              <td data-label={t("when", "When")}>{formatInZone(r.at, tz)}{r.count > 1 && r.lastAt ? <small className="live-muted"> → {formatTime(r.lastAt, tz)}</small> : null}</td>
              <td data-label={t("event", "Event")}>{EVENT_LABELS[r.type] || r.type}</td>
              <td data-label={t("attempts", "Attempts")} className="num">{formatCount(r.count || 1)}</td>
              <td data-label={t("detail", "Detail")}>{[r.method === "device_verification" ? t("viaCode", "via new-device code") : null, r.reasonCode].filter(Boolean).join(" · ") || "—"}</td>
            </tr>
          ))}</tbody>
        </table></div>
      )}
      <p className="ia-note">{t("loginRule", "Only real sign-in requests count. Reloading a page or opening a tab with a saved login is never a login. Repeated failures within 10 minutes are grouped.")}</p>
    </>
  );
};

/* ── G / H ── */
const SecurityTab = ({ vk, tz, t, mode }) => {
  const [state, , retry] = useLoader((signal) => getIndividualSecurityAPI(vk, { signal }), [vk], true);
  if (state.error) return <Failed error={state.error} retry={retry} t={t} />;
  if (!state.data) return <Busy t={t} />;
  const s = state.data;
  if (mode === "restrictions") {
    return (
      <>
        <dl className="ia-dl"><Item label={t("restrictionStatus", "Restriction status")}>{t(`restr_${s.restrictionStatus}`, s.restrictionStatus.replace(/_/g, " "))}</Item></dl>
        {!s.restrictions.length ? <Empty text={t("noRestrictions", "No suspension or restriction on the linked accounts.")} /> : (
          <div className="ia-table-wrap"><table className="ia-table">
            <thead><tr><th>{t("level", "Level")}</th><th>{t("cStatus", "Status")}</th><th>{t("period", "Period")}</th><th>{t("reason", "Reason")}</th><th>{t("by", "By")}</th></tr></thead>
            <tbody>{s.restrictions.map((r) => (
              <tr key={r.id}>
                <td data-label={t("level", "Level")}>{r.level === "User" ? t("fullBlock", "Account blocked") : `${t("cappedTo", "Capped below")} ${r.level}`}</td>
                <td data-label={t("cStatus", "Status")}>{r.status}{r.origin === "automatic" ? ` (${t("automatic", "automatic")})` : ""}</td>
                <td data-label={t("period", "Period")}>{formatInZone(r.startAt, tz)} → {r.endAt ? formatInZone(r.endAt, tz) : t("unlimited", "unlimited")}</td>
                <td data-label={t("reason", "Reason")}>{r.reason}</td>
                <td data-label={t("by", "By")}>{r.createdBy || "—"}{r.liftedBy ? ` · ${t("liftedBy", "lifted by")} ${r.liftedBy}` : ""}</td>
              </tr>
            ))}</tbody>
          </table></div>
        )}
        <p className="ia-note">{t("restrictionNote", "Account restrictions are the existing Suspension system, enforced by the server on every request. Manage them from Community Management → user card → Suspend.")}</p>
      </>
    );
  }
  return (
    <>
      <dl className="ia-dl">
        <Item label={t("riskLevel", "Risk level")}><RiskBadge level={s.riskLevel} t={t} /></Item>
        <Item label={t("cSecurity", "Security")}>{formatCount(s.securityEventCount)} {t("openEvents30", "event(s) in the last 30 days")}</Item>
        <Item label={t("rateLimited", "Rate-limited windows")}>{formatCount(s.rateLimitedCount)}</Item>
      </dl>
      {!s.events.length ? <Empty text={t("noSecurity", "No Security Center event is linked to this browser or its accounts.")} /> : (
        <div className="ia-table-wrap"><table className="ia-table">
          <thead><tr><th>{t("lastSeenEvt", "Last seen")}</th><th>{t("type", "Type")}</th><th>{t("severity", "Severity")}</th><th>{t("cStatus", "Status")}</th><th className="num">{t("occurrences", "Occurrences")}</th><th>{t("linkedBy", "Linked by")}</th></tr></thead>
          <tbody>{s.events.map((e) => (
            <tr key={e.id}>
              <td data-label={t("lastSeenEvt", "Last seen")}>{formatInZone(e.lastSeen, tz)}</td>
              <td data-label={t("type", "Type")}><div className="ia-cell-main"><span>{e.type}</span><small>{e.message}</small></div></td>
              <td data-label={t("severity", "Severity")}>{e.severity}</td>
              <td data-label={t("cStatus", "Status")}>{e.status}</td>
              <td data-label={t("occurrences", "Occurrences")} className="num">{formatCount(e.count)}</td>
              <td data-label={t("linkedBy", "Linked by")}>{e.link === "this_browser" ? t("thisBrowserLink", "this browser") : t("accountLink", "a linked account")}</td>
            </tr>
          ))}</tbody>
        </table></div>
      )}
      <p className="ia-note">{t("securityNote", "Events come from the existing Security Center (deduplicated, with its own alert thresholds). Risk = highest severity among open events of the last 30 days.")} <Link to="/elakay/security-center">{t("openSecurityCenter", "Open Security Center")}</Link></p>
    </>
  );
};

const VisitorDetail = ({ visitorKey: vk, initialTab = "overview", onHide, onChanged, t }) => {
  const [tab, setTab] = useState(TABS.some(([k]) => k === initialTab) ? initialTab : "overview");
  const [state, , reload] = useLoader((signal) => getIndividualVisitorAPI(vk, { signal }), [vk], true);
  const d = state.data;
  const tz = d?.timezone;
  const reloadAll = useCallback(() => { reload(); onChanged?.(); }, [reload, onChanged]);
  const tabs = useMemo(() => TABS.map(([key, label]) => [key, t(`tab_${key}`, label)]), [t]);
  const onKeyTabs = (e) => {
    const i = TABS.findIndex(([k]) => k === tab);
    if (e.key === "ArrowRight") { e.preventDefault(); setTab(TABS[(i + 1) % TABS.length][0]); }
    if (e.key === "ArrowLeft") { e.preventDefault(); setTab(TABS[(i - 1 + TABS.length) % TABS.length][0]); }
  };
  return (
    <Modal show onHide={onHide} size="xl" fullscreen="md-down" centered scrollable className="ia-modal" aria-labelledby="ia-detail-title">
      <Modal.Header closeButton>
        <Modal.Title as="h2" id="ia-detail-title" className="ia-modal-title">
          <span>{t("detailTitle", "Visitor record")} <span className="ia-id">{shortId(vk)}</span></span>
          {d && (
            <span className="ia-badges">
              <StatusBadge status={d.visitor.status} t={t} />
              <TypeBadge type={d.visitor.visitorType} t={t} />
              {d.visitor.associatedAccounts?.[0] && <span className="ia-badge">{d.visitor.associatedAccounts[0].name}</span>}
              <RiskBadge level={d.visitor.riskLevel} t={t} />
            </span>
          )}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {d && !d.saveHistory && <div className="ia-banner ia-banner-off" role="status">{t("offBannerShort", "“Save Visitor History” is OFF — showing previously recorded data only.")}</div>}
        <div className="ia-tabs" role="tablist" aria-label={t("sections", "Sections")} onKeyDown={onKeyTabs}>
          {tabs.map(([key, label]) => (
            <button key={key} type="button" role="tab" id={`ia-tab-${key}`} aria-selected={tab === key} aria-controls={`ia-panel-${key}`} tabIndex={tab === key ? 0 : -1} onClick={() => setTab(key)}>{label}</button>
          ))}
        </div>
        <div className="ia-panel" role="tabpanel" id={`ia-panel-${tab}`} aria-labelledby={`ia-tab-${tab}`}>
          {state.error ? <Failed error={state.error} retry={reload} t={t} /> : !d ? <Busy t={t} /> : (
            <>
              {tab === "overview" && <Overview d={d} tz={tz} t={t} />}
              {tab === "devices" && <Devices vk={vk} d={d} tz={tz} t={t} />}
              {tab === "accounts" && <Accounts vk={vk} d={d} tz={tz} t={t} reload={reloadAll} />}
              {tab === "historypage" && <HistoryPageTab vk={vk} tz={tz} t={t} />}
              {tab === "historylog" && <HistoryLogTab vk={vk} t={t} />}
              {tab === "logins" && <LoginsTab vk={vk} tz={tz} t={t} />}
              {tab === "security" && <SecurityTab vk={vk} tz={tz} t={t} mode="security" />}
              {tab === "restrictions" && <SecurityTab vk={vk} tz={tz} t={t} mode="restrictions" />}
            </>
          )}
        </div>
      </Modal.Body>
      <Modal.Footer>
        <button type="button" className="live-btn live-btn-ghost" onClick={onHide}>{t("close", "Close")}</button>
      </Modal.Footer>
    </Modal>
  );
};

export default VisitorDetail;
